/**
 * Hugs Health — PDP buy box interactivity.
 * - Thumbnail click swaps the main gallery image.
 * - Quantity pills (1..max) recompute both Subscribe and One-Time prices
 *   client-side, formatted using the shop's real money_format.
 * - Subscribe / One-Time is a simple radio pair; selecting one is purely
 *   visual/informational for the price shown — quantity always applies to
 *   both, and the choice of which radio is checked determines whether the
 *   add-to-cart call includes a selling_plan.
 * - A sticky add-to-cart bar appears once the main CTA scrolls out of view.
 * - "Start Now" (main or sticky) adds the product (with quantity + selling
 *   plan if Subscribe is selected) and, if the bundle checkbox is checked,
 *   the upsell product's variant, via Shopify's Ajax Cart API in one call.
 */

/** Minimal formatter for Shopify's `shop.money_format` token syntax. */
function hugsFormatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = cents || 0;
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function defaultTo(value, defaultValue) {
    return value == null || value !== value ? defaultValue : value;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');
    if (isNaN(number) || number == null) return '0';
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const cents2 = parts[1] ? decimal + parts[1] : '';
    return dollars + cents2;
  }

  if (!format) format = '${{amount}}';

  let result;
  switch (format.match(placeholderRegex)?.[1]) {
    case 'amount':
      result = formatWithDelimiters(value, 2);
      break;
    case 'amount_no_decimals':
      result = formatWithDelimiters(value, 0);
      break;
    case 'amount_with_comma_separator':
      result = formatWithDelimiters(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      result = formatWithDelimiters(value, 0, '.', ',');
      break;
    default:
      result = formatWithDelimiters(value, 2);
  }

  return format.replace(placeholderRegex, result);
}

function initHugsBuybox(root) {
  root.querySelectorAll('[data-hugs-buybox]:not([data-hugs-initialized])').forEach((buybox) => {
    buybox.setAttribute('data-hugs-initialized', 'true');

    const mainImageEl = buybox.querySelector('[data-hugs-main-image-el]');
  const mainImageWrap = buybox.querySelector('[data-hugs-main-image]');

  buybox.querySelectorAll('[data-hugs-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const full = thumb.dataset.full;
      const alt = thumb.dataset.alt || '';
      if (!full) return;

      if (mainImageEl) {
        mainImageEl.src = full;
        mainImageEl.alt = alt;
      } else if (mainImageWrap) {
        mainImageWrap.innerHTML = '';
        const img = document.createElement('img');
        img.src = full;
        img.alt = alt;
        img.setAttribute('data-hugs-main-image-el', '');
        mainImageWrap.appendChild(img);
      }

      buybox.querySelectorAll('[data-hugs-thumb]').forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });

  // ---- Quantity pills + live price recompute ----------------------------
  const moneyFormat = buybox.dataset.moneyFormat || '${{amount}}';
  const variantPrice = parseInt(buybox.dataset.variantPrice, 10) || 0;
  const subscribeUnitPrice = parseInt(buybox.dataset.subscribeUnitPrice, 10) || 0;
  let currentQuantity = parseInt(buybox.dataset.defaultQuantity, 10) || 1;

  const subscribeDetailEl = buybox.querySelector('[data-hugs-subscribe-detail]');
  const subscribePriceEl = buybox.querySelector('[data-hugs-subscribe-price]');
  const subscribeCompareEl = buybox.querySelector('[data-hugs-subscribe-compare]');
  const subscribePerDayEl = buybox.querySelector('[data-hugs-subscribe-perday]');
  const onetimeDetailEl = buybox.querySelector('[data-hugs-onetime-detail]');
  const onetimePriceEl = buybox.querySelector('[data-hugs-onetime-price]');
  const onetimePerDayEl = buybox.querySelector('[data-hugs-onetime-perday]');
  const stickyPriceEl = buybox.closest('section')?.querySelector('[data-hugs-sticky-price]');

  function renderPrices() {
    const subscribeTotal = subscribeUnitPrice * currentQuantity;
    const onetimeTotal = variantPrice * currentQuantity;
    const plural = currentQuantity !== 1 ? 's' : '';

    if (subscribeDetailEl) subscribeDetailEl.textContent = `${currentQuantity} bottle${plural} every ${buybox.dataset.subscribeFrequency || '4 weeks'}`;
    if (subscribePriceEl) subscribePriceEl.textContent = hugsFormatMoney(subscribeTotal, moneyFormat);
    if (subscribeCompareEl) {
      if (subscribeTotal !== onetimeTotal) {
        subscribeCompareEl.textContent = hugsFormatMoney(onetimeTotal, moneyFormat);
        subscribeCompareEl.style.display = '';
      } else {
        subscribeCompareEl.style.display = 'none';
      }
    }
    if (subscribePerDayEl) subscribePerDayEl.textContent = hugsFormatMoney(Math.round(subscribeTotal / 28), moneyFormat) + '/day';

    if (onetimeDetailEl) onetimeDetailEl.textContent = `${currentQuantity} bottle${plural} delivered once`;
    if (onetimePriceEl) onetimePriceEl.textContent = hugsFormatMoney(onetimeTotal, moneyFormat);
    if (onetimePerDayEl) onetimePerDayEl.textContent = hugsFormatMoney(Math.round(onetimeTotal / 28), moneyFormat) + '/day';

    if (stickyPriceEl) {
      const modeInput = buybox.querySelector('[data-hugs-mode-input]:checked');
      const activeTotal = modeInput && modeInput.value === 'onetime' ? onetimeTotal : subscribeTotal;
      stickyPriceEl.textContent = hugsFormatMoney(activeTotal, moneyFormat);
    }
  }

  buybox.querySelectorAll('[data-hugs-qty-pill]').forEach((pill) => {
    pill.addEventListener('click', () => {
      currentQuantity = parseInt(pill.dataset.value, 10) || 1;
      buybox.querySelectorAll('[data-hugs-qty-pill]').forEach((p) => {
        p.classList.remove('selected');
        p.setAttribute('aria-pressed', 'false');
      });
      pill.classList.add('selected');
      pill.setAttribute('aria-pressed', 'true');
      renderPrices();
    });
  });

  buybox.querySelectorAll('[data-hugs-mode-input]').forEach((input) => {
    input.addEventListener('change', () => {
      buybox.querySelectorAll('[data-hugs-purchase-option]').forEach((opt) => opt.classList.remove('selected'));
      if (input.checked) input.closest('[data-hugs-purchase-option]').classList.add('selected');

      const purchaseCard = buybox.querySelector('[data-hugs-purchase-card]');
      if (purchaseCard) purchaseCard.classList.toggle('selected', input.value === 'subscribe' && input.checked);

      renderPrices();
    });
  });

  renderPrices();

  // ---- Sticky bar visibility ---------------------------------------------
  const sectionEl = buybox.closest('section');
  const stickyBar = sectionEl ? sectionEl.querySelector('[data-hugs-sticky-bar]') : null;
  const mainCta = buybox.querySelector('.hugs-buybox__cta');
  if (stickyBar && mainCta && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { rootMargin: '0px 0px -20% 0px' }
    );
    observer.observe(mainCta);
  }

  // ---- Add to cart --------------------------------------------------------
  const statusEl = buybox.querySelector('[data-hugs-cta-status]');
  const bundleCheckbox = buybox.querySelector('[data-hugs-bundle-checkbox]');
  const cartAddUrl = buybox.dataset.cartAddUrl || '/cart/add.js';
  const cartUrl = buybox.dataset.cartUrl || '/cart';

  const addToCart = async (triggerButton) => {
    const variantId = triggerButton.dataset.variantId;
    if (!variantId) return;

    const modeInput = buybox.querySelector('[data-hugs-mode-input]:checked');
    const isSubscribe = !modeInput || modeInput.value !== 'onetime';
    const sellingPlanId = isSubscribe ? triggerButton.dataset.sellingPlanId : null;

    const items = [
      {
        id: parseInt(variantId, 10),
        quantity: currentQuantity,
        ...(sellingPlanId ? { selling_plan: parseInt(sellingPlanId, 10) } : {}),
      },
    ];

    if (bundleCheckbox && bundleCheckbox.checked && bundleCheckbox.value) {
      items.push({ id: parseInt(bundleCheckbox.value, 10), quantity: 1 });
    }

    const allButtons = (sectionEl || buybox).querySelectorAll('[data-hugs-add-to-cart]');
    allButtons.forEach((btn) => (btn.disabled = true));
    if (statusEl) {
      statusEl.textContent = 'Adding to cart…';
      statusEl.classList.remove('is-error', 'is-success');
    }

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error((errorData && errorData.description) || 'Could not add this to your cart.');
      }

      if (statusEl) {
        statusEl.textContent = 'Added! Redirecting to your cart…';
        statusEl.classList.add('is-success');
      }
      window.location.href = cartUrl;
    } catch (err) {
      allButtons.forEach((btn) => (btn.disabled = false));
      if (statusEl) {
        statusEl.textContent = err.message || 'Something went wrong — please try again.';
        statusEl.classList.add('is-error');
      }
    }
  };

  (sectionEl || buybox).querySelectorAll('[data-hugs-add-to-cart]').forEach((btn) => {
    btn.addEventListener('click', () => addToCart(btn));
  });
  });
}

// Run on normal page load...
initHugsBuybox(document);

// ...and re-run whenever the Shopify theme editor hot-swaps a section's
// markup after a settings change, since that AJAX update doesn't re-execute
// this script and would otherwise leave the new thumbnails/buttons inert.
document.addEventListener('shopify:section:load', (event) => {
  initHugsBuybox(event.target);
});
